import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    GridToolbarContainer
} from '@mui/x-data-grid';
import {DataGridPro} from "@mui/x-data-grid-pro";
import {
    IconButton,
    Box,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Select,
    MenuItem,
    Alert, Stack, Paper, CircularProgress, Autocomplete,
    FormControl,
    InputLabel,
    Tabs,
    Tab
} from '@mui/material';
import UnlinkedProductDropdown from './UnlinkedProductDropdown';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../AxiosInstance";
import convertDateStringToDate, {formatDate, getCurrentDate} from '../Utilities/dateUtility';
import {getUnitCogsValidationError, isPositiveWholeNumber, validateStartDate} from "../Utilities/validation";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WarningIcon from '@mui/icons-material/Warning';
import LinearProgress from "@mui/material/LinearProgress";
import { useColumnWidths } from '../Utilities/columnWidthUtils';
import ProductCogsMonthlyView from './ProductCogsMonthlyView';
import { formatFiscalYearLabel, getAvailableFiscalYears, getCurrentFiscalYear } from '../Utilities/productCogsMonthly';

const defaultColumnWidths = {
    skuNumber: 100,
    skuDescription: 200,
    groupName: 150
};

function EditToolbar(props) {
    const {
        selectedProduct,
        onAdd,
        onDelete,
        onEdit
    } = props;

    return (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Create">
                <span>
                    <IconButton aria-label="createProduct" onClick={onAdd}>
                        <AddIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Edit">
                <span>
                    <IconButton aria-label="edit" onClick={onEdit} disabled={!selectedProduct}>
                        <EditIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Delete">
                <span>
                    <IconButton aria-label="delete" onClick={onDelete} disabled={!selectedProduct}>
                        <DeleteIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </GridToolbarContainer>
    );
}

function ProductTable() {
    const [rows, setRows] = useState([]);
    const [snackbar, setSnackbar] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCogs, setSelectedCogs] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [futureWeeksOptions, setFutureWeeksOptions] = useState([]);
    const [editData, setEditData] = useState({
        endDate: '',
        unitCogs: '',
        unitCogsStartDate: '',
        externalProductId: null,
        brand: '',
        groupName: '',
        packSize: '1'
    });

    const [cogsProductId, setCogsProductId] = useState(null);

    const [addCogsDialogOpen, setAddCogsDialogOpen] = useState(false);
    const [newCogsData, setNewCogsData] = useState({
        unitCogs: '',
        startDate: '',
        endDate: ''
    });
    const [cogsErrorTexts, setCogsErrorTexts] = useState({
        unitCogs: '',
        startDate: ''
    });

    // New state for integration features
    const [hasIntegrationProviders, setHasIntegrationProviders] = useState(false);
    const [externalProducts, setExternalProducts] = useState([]);
    const [loadingExternalProducts, setLoadingExternalProducts] = useState(false);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const { columnWidths, handleColumnResize } = useColumnWidths('product', defaultColumnWidths);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list');
    const [startMonth, setStartMonth] = useState(1);
    const [fiscalYear, setFiscalYear] = useState(() => getCurrentFiscalYear(1));
    const [isSubmittingEditProduct, setIsSubmittingEditProduct] = useState(false);
    const [isSubmittingCreateProduct, setIsSubmittingCreateProduct] = useState(false);
    const [isSubmittingAddCogs, setIsSubmittingAddCogs] = useState(false);
    const isSubmittingEditProductRef = useRef(false);
    const isSubmittingCreateProductRef = useRef(false);
    const isSubmittingAddCogsRef = useRef(false);

    const resetCreateData = () => {
        setCreateData({
            skuNumber: '',
            skuDescription: '',
            brand: '',
            unitCogs: '',
            startDate: getCurrentDate(),
            externalProductId: null,
            groupName: '',
            packSize: '1'
        });
    };

    const [createData, setCreateData] = useState({
        skuNumber: '',
        skuDescription: '',
        brand: '',
        unitCogs: '',
        startDate: getCurrentDate(),
        externalProductId: null,
        groupName: '',
        packSize: '1'
    });

    const [createErrorTexts, setCreateErrorTexts] = useState({
        skuNumber: '',
        skuDescription: '',
        brand: '',
        unitCogs: '',
        startDate: '',
        packSize: ''
    });

    const [endDateErrorText, setEndDateErrorText] = useState('');
    const [packSizeErrorText, setPackSizeErrorText] = useState('');

    useEffect(() => {
        getProducts();
        checkIntegrationProviders();
    }, []);

    // Function to check if integration providers exist
    const checkIntegrationProviders = async () => {
        try {
            const response = await axiosInstance.get('/settings');
            setHasIntegrationProviders(
                response.status === 200 && response.data?.isEnabled === true
            );
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setHasIntegrationProviders(false);
            } else {
                console.error('Error checking integration providers:', error);
                setHasIntegrationProviders(false);
            }
        }
    };

    // Function to fetch external products
    const fetchExternalProducts = async () => {
        if (!hasIntegrationProviders) {
            setExternalProducts([]);
            return;
        }
        
        setLoadingExternalProducts(true);
        try {
            const response = await axiosInstance.get('/products/externalunlinked');
            setExternalProducts(response.data);
        } catch (error) {
            console.error('Error fetching external products:', error);
        } finally {
            setLoadingExternalProducts(false);
        }
    };

    const fetchAvailableBrands = async () => {
        setLoadingBrands(true);
        try {
            const response = await axiosInstance.get('/products/brands');
            setAvailableBrands(response.data);
        } catch (error) {
            console.error('Error fetching brands:', error);
        } finally {
            setLoadingBrands(false);
        }
    };

    const fetchAvailableGroups = async () => {
        setLoadingGroups(true);
        try {
            const response = await axiosInstance.get('/products/groups');
            setAvailableGroups(response.data);
        } catch (error) {
            console.error('Error fetching groups:', error);
        } finally {
            setLoadingGroups(false);
        }
    };

    // Helper function to get provider name from external product ID
    const getIntegrationProviderName = (externalProductId) => {
        if (!hasIntegrationProviders || !externalProductId) {
            return null;
        }
        
        const selectedExternal = externalProducts.find(
            ext => ext.externalProductId === externalProductId
        );
        
        return selectedExternal ? selectedExternal.provider : null;
    };

    const getProducts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/products/products-with-weeks");

            setFutureWeeksOptions(response.data.availableWeeks || []);

            const resolvedStartMonth = response.data.startMonth > 0 ? response.data.startMonth : 1;
            setStartMonth(resolvedStartMonth);
            setFiscalYear(getCurrentFiscalYear(resolvedStartMonth));

            const productRows = response.data.products.map(product => ({
                id: `product-${product.productId}`,
                productId: product.productId,
                skuNumber: product.skuNumber,
                skuDescription: product.skuDescription,
                brand: product.brand,
                hierarchyLevel1: product.hierarchyLevel1,
                hierarchyLevel2: product.hierarchyLevel2,
                hierarchyLevel3: product.hierarchyLevel3,
                startDate: product.startDate,
                endDate: product.endDate,
                externalProductId: product.externalProductId,
                integrationProviderName: product.integrationProviderName,
                groupName: product.groupName,
                packSize: product.packSize ?? 1,
                cogs: product.cogs.map(cogs => ({
                    id: `cogs-${cogs.productCogsId}`,
                    productId: product.productId,
                    productCogsId: cogs.productCogsId,
                    unitCogs: cogs.unitCogs,
                    startDate: cogs.startDate,
                    endDate: cogs.endDate
                }))
            }));

            setRows(productRows);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = async () => {
        await Promise.all([fetchAvailableBrands(), fetchAvailableGroups()]);
        setCreateDialogOpen(true);
    };

    const handleDeleteClick = async () => {
        if (!selectedProduct) return;

        try {
            await axiosInstance.delete(`/products/${selectedProduct.productId}`);
            setRows((prevRows) => prevRows.filter((row) => row.productId !== selectedProduct.productId));
            setSnackbar({children: 'Product successfully deleted', severity: 'success'});
        } catch (error) {
            setSnackbar({children: 'Error deleting product', severity: 'error'});
        }
    };

    const handleEditClick = async () => {
        if (!selectedProduct) return;

        setEditData({
            endDate: selectedProduct.endDate ? formatDateForInput(selectedProduct.endDate) : '',
            unitCogs: selectedProduct.futureUnitCogs || '',
            unitCogsStartDate: selectedProduct.futureUnitCogsStartDate,
            externalProductId: selectedProduct.externalProductId,
            brand: selectedProduct.brand || '',
            groupName: selectedProduct.groupName || '',
            packSize: String(selectedProduct.packSize ?? 1)
        });

        try {
            const response = await axiosInstance.get('/calendar');
            setFutureWeeksOptions(response.data);
            
            if (hasIntegrationProviders && !selectedProduct.integrationProviderName) {
                await fetchExternalProducts();
            }
            
            await Promise.all([fetchAvailableBrands(), fetchAvailableGroups()]);
        } catch (error) {
            console.error('Error fetching future weeks options:', error);
        }

        setDialogOpen(true);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().slice(0, 10); // Only keep 'yyyy-MM-dd'
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        resetErrorMessages();
    };

    const resetErrorMessages = () => {
        setEndDateErrorText('');
        setPackSizeErrorText('');
    };

    const validateFields = () => {
        let isValid = true;

        let endDateProvided = !!editData.endDate;

        const currentDate = new Date();
        const startDate = new Date(selectedProduct.startDate); // Get the product's start date
        const endDate = endDateProvided ? new Date(editData.endDate) : null; // Get the provided end date

        // Prevent editing of expired products
        if (selectedProduct.endDate && new Date(selectedProduct.endDate) < currentDate) {
            setSnackbar({children: 'An expired product cannot be edited', severity: 'error'});
            handleDialogClose();
            return false;
        }

        // Check if the end date is before the current date (past date)
        if (endDateProvided && endDate < currentDate) {
            setEndDateErrorText('Product End Date cannot be set to a date in the past');
            isValid = false;
        } else if (endDateProvided && endDate < startDate) {
            // Check if the end date is before the start date
            setEndDateErrorText('End Date cannot be earlier than Start Date');
            isValid = false;
        } else {
            setEndDateErrorText(''); // Clear error if valid
        }

        if (!isPositiveWholeNumber(editData.packSize)) {
            setPackSizeErrorText('Pack Size must be a whole number greater than 0');
            isValid = false;
        } else {
            setPackSizeErrorText('');
        }

        return isValid;
    };
    
    const handleDialogSubmit = async () => {
        if (isSubmittingEditProductRef.current) {
            return;
        }

        if (!validateFields()) {
            return;
        }

        isSubmittingEditProductRef.current = true;
        setIsSubmittingEditProduct(true);
        setDialogOpen(false);

        const updateRequest = {
            endDate: editData.endDate ? new Date(editData.endDate) : null,
            externalProductId: hasIntegrationProviders ? (editData.externalProductId || null) : null,
            brand: editData.brand,
            groupName: editData.groupName || null,
            packSize: Number(editData.packSize)
        };

        try {
            await axiosInstance.put(`/products/${selectedProduct.productId}`, updateRequest);

            const integrationProviderName = selectedProduct.integrationProviderName || 
                getIntegrationProviderName(editData.externalProductId);

            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.productId === selectedProduct.productId
                        ? {
                            ...row,
                            ...updateRequest,
                            productId: selectedProduct.productId,
                            futureUnitCogsStartDate: updateRequest.futureUnitCogsStartDate,
                            integrationProviderName,
                            brand: updateRequest.brand,
                            groupName: updateRequest.groupName,
                            packSize: updateRequest.packSize
                        }
                        : row
                )
            );

            setSnackbar({children: 'Product successfully updated', severity: 'success'});
            
            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (error) {
            setSnackbar({children: 'Error updating product', severity: 'error'});
        } finally {
            setIsSubmittingEditProduct(false);
            isSubmittingEditProductRef.current = false;
        }
    };

    const handleCreateDialogClose = () => {
        setCreateDialogOpen(false);
        resetCreateData();
        resetCreateErrorMessages();
    };

    const resetCreateErrorMessages = () => {
        setCreateErrorTexts({
            skuNumber: '',
            skuDescription: '',
            brand: '',
            unitCogs: '',
            startDate: '',
            packSize: ''
        });
    };

    const validateCreateFields = () => {
        const errors = {...createErrorTexts};
        let isValid = true;

        if (!createData.skuNumber) {
            errors.skuNumber = 'SKU Number is required';
            isValid = false;
        } else {
            errors.skuNumber = '';
        }

        if (!createData.skuDescription) {
            errors.skuDescription = 'Description is required';
            isValid = false;
        } else {
            errors.skuDescription = '';
        }

        if (!createData.brand) {
            errors.brand = 'Brand is required';
            isValid = false;
        } else {
            errors.brand = '';
        }

        const unitCogsError = getUnitCogsValidationError(createData.unitCogs);
        if (unitCogsError) {
            errors.unitCogs = unitCogsError;
            isValid = false;
        } else {
            errors.unitCogs = '';
        }

        const isValidStartDate = validateStartDate(createData.startDate);
        if (!isValidStartDate) {
            errors.startDate = 'Start Date cannot be in the past';
            isValid = false;
        } else {
            errors.startDate = '';
        }

        if (!isPositiveWholeNumber(createData.packSize)) {
            errors.packSize = 'Pack Size must be a whole number greater than 0';
            isValid = false;
        } else {
            errors.packSize = '';
        }

        setCreateErrorTexts(errors);
        return isValid;
    };

    const handleCreateDialogSubmit = async () => {
        if (isSubmittingCreateProductRef.current) {
            return;
        }

        if (!validateCreateFields()) {
            return;
        }

        isSubmittingCreateProductRef.current = true;
        setIsSubmittingCreateProduct(true);

        const newProduct = {
            skuNumber: createData.skuNumber,
            skuDescription: createData.skuDescription,
            brand: createData.brand,
            unitCogs: parseFloat(createData.unitCogs),
            startDate: createData.startDate,
            externalProductId: hasIntegrationProviders ? (createData.externalProductId || null) : null,
            groupName: createData.groupName || null,
            packSize: Number(createData.packSize)
        };

        try {
            const response = await axiosInstance.post('/products', newProduct);
            const createdProduct = response.data;

            createdProduct.id = `product-${createdProduct.productId}`;
            createdProduct.groupName = createData.groupName || null;
            createdProduct.packSize = createdProduct.packSize ?? newProduct.packSize;

            // Add integration provider name if applicable
            if (hasIntegrationProviders && createdProduct.externalProductId) {
                createdProduct.integrationProviderName = getIntegrationProviderName(createdProduct.externalProductId);
            }

            if (createdProduct.cogs) {
                createdProduct.cogs = createdProduct.cogs.map(cogs => ({
                    ...cogs,
                    id: `cogs-${cogs.productCogsId}`
                }));
            }

            setRows((prevRows) => [createdProduct, ...prevRows]);

            resetCreateData();
            setCreateDialogOpen(false);
            resetCreateErrorMessages();
            setSnackbar({ children: 'Product successfully created', severity: 'success' });
            
            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (error) {
            const errorMessage = error.response?.data || 'An error occurred while creating the product.';
            setSnackbar({ children: errorMessage, severity: 'error' });
        } finally {
            setIsSubmittingCreateProduct(false);
            isSubmittingCreateProductRef.current = false;
        }
    };

    const handleRowSelection = (selectionModel) => {
        if (selectionModel.length > 0) {
            const selectedRow = rows.find(row => row.id === selectionModel[0]);
            setSelectedProduct(selectedRow || null);
        } else {
            setSelectedProduct(null);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(null);
    };

    // Integration cell rendering functions
    const renderIntegrationCell = (params) => {
        if (params.row.integrationProviderName)
            return params.row.integrationProviderName;
        
        return (
            <Tooltip title="Not linked">
                <WarningIcon color="warning" />
            </Tooltip>
        );
    };

    // Base product columns
    const baseProductColumns = [
        { field: "skuNumber", headerName: "SKU", width: columnWidths.skuNumber },
        { field: "skuDescription", headerName: "Description", width: columnWidths.skuDescription },
        { field: "brand", headerName: "Brand", width: 100 },
        { field: "packSize", headerName: "Pack Size", type: "number", width: 100 },
        {
            field: 'startDate',
            headerName: 'Start Date',
            type: 'date',
            width: 100,
            valueFormatter: (value) => formatDate(value)
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            type: 'date',
            width: 100,
            valueFormatter: (value) => {
                if (!value) {
                    return '';
                }
                return formatDate(value);
            }
        },
    ];

    // Integration column definition
    const integrationColumn = {
        field: 'externalProductId',
        headerName: 'Integration',
        description: 'Products can be linked to 3rd party integrations. Once linked, the integration cannot be changed.',
        width: 150,
        renderCell: renderIntegrationCell,
    };
    
    const groupColumn = {
        field: 'groupName',
        headerName: 'Group',
        width: columnWidths.groupName,
    };
    
    // Final product columns based on whether integration is enabled
    const productColumns = hasIntegrationProviders ? 
        [...baseProductColumns.slice(0, 2), integrationColumn, groupColumn, ...baseProductColumns.slice(2)] : 
        [...baseProductColumns.slice(0, 2), groupColumn, ...baseProductColumns.slice(2)];

    const cogsColumns = [
        { 
            field: "unitCogs", 
            headerName: "Unit COGS",
            width: 120, 
            type: "number",
            valueFormatter: (value) => {
                const formattedValue = parseFloat(value).toFixed(3);
                return formattedValue.endsWith('0') ? '£' + parseFloat(value).toFixed(2) : '£' + parseFloat(value).toFixed(3);
            },
        },
        {
            field: 'startDate',
            headerName: 'Start Date',
            type: 'date',
            width: 100,
            valueFormatter: (value) => formatDate(value)
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            type: 'date',
            width: 100,
            valueFormatter: (value) => {
                if (!value) {
                    return '';
                }
                return formatDate(value);
            }
        },
    ];

    const handleCogsRowSelection = (selectionModel, allRows) => {
        const selectedRow = allRows.find(row => row.id === selectionModel[0]);
        setSelectedCogs(selectedRow || null);
    };

    const handleAddCogs = (productId) => {
        setCogsProductId(productId);
        setNewCogsData({ unitCogs: '', startDate: '', endDate: '' });
        setCogsErrorTexts({ unitCogs: '', startDate: '' });
        setIsSubmittingAddCogs(false);
        isSubmittingAddCogsRef.current = false;
        setAddCogsDialogOpen(true);
    };

    const handleAddCogsDialogClose = () => {
        if (isSubmittingAddCogs) {
            return;
        }

        setAddCogsDialogOpen(false);
        setIsSubmittingAddCogs(false);
        isSubmittingAddCogsRef.current = false;
    };

    const validateCogsFields = () => {
        let isValid = true;
        let errors = { unitCogs: '', startDate: '' };

        const unitCogsError = getUnitCogsValidationError(newCogsData.unitCogs);
        if (unitCogsError) {
            errors.unitCogs = unitCogsError;
            isValid = false;
        }
        if (!newCogsData.startDate) {
            errors.startDate = 'Start Date is required';
            isValid = false;
        }

        setCogsErrorTexts(errors);
        return isValid;
    };

    const handleAddCogsSubmit = async () => {
        if (isSubmittingAddCogsRef.current) {
            return;
        }

        if (!validateCogsFields()) return;
        if (!cogsProductId) {
            setSnackbar({ children: "Error: No Product selected for COGS", severity: "error" });
            return;
        }

        isSubmittingAddCogsRef.current = true;
        setIsSubmittingAddCogs(true);
        try {
            const requestData = {
                unitCogs: parseFloat(newCogsData.unitCogs),
                startDate: newCogsData.startDate,
                endDate: newCogsData.endDate || null
            };

            await axiosInstance.post(`/products/${cogsProductId}/cogs`, requestData);

            await refreshProductCogs(cogsProductId);

            setSnackbar({ children: "COGS successfully added", severity: "success" });
            setAddCogsDialogOpen(false);
        } catch (error) {
            console.error("Error response:", error.response?.data); // Debugging log

            let errorMessage = "An error occurred while adding COGS.";

            if (error.response) {
                if (error.response.status === 400) {
                    if (typeof error.response.data === "string") {
                        errorMessage = error.response.data;
                    } else if (error.response.data?.errors) {
                        const uniqueErrors = new Set(Object.values(error.response.data.errors).flat());
                        errorMessage = [...uniqueErrors].join(", ");
                    }
                } else {
                    errorMessage = `Error: ${error.response.status} - ${error.response.statusText}`;
                }
            }

            setSnackbar({ children: errorMessage, severity: "error" });
            // Allow retry after an error without reopening the dialog.
            setIsSubmittingAddCogs(false);
            isSubmittingAddCogsRef.current = false;
        }
    };

    const handleDeleteCogs = async () => {
        if (!selectedCogs) return;

        try {
            await axiosInstance.delete(`/products/${selectedCogs.productId}/cogs/${selectedCogs.productCogsId}`);

            await refreshProductCogs(selectedCogs.productId);

            setSelectedCogs(null);
            setSnackbar({ children: "COGS successfully deleted", severity: "success" });
        } catch (error) {
            const errorMessage =
                error.response?.data || "An unexpected error occurred while deleting COGS.";
            setSnackbar({ children: errorMessage, severity: "error" });
        }
    };

    const refreshProductCogs = async (productId) => {
        try {
            const response = await axiosInstance.get(`/products/${productId}`);

            setRows(prevRows =>
                prevRows.map(row =>
                    row.productId === productId
                        ? {
                            ...row,
                            cogs: response.data.cogs.map(cogs => ({
                                ...cogs,
                                id: `cogs-${cogs.productCogsId}`
                            }))
                        }
                        : row
                )
            );
        } catch (error) {
            console.error("Error refreshing COGS data:", error);
        }
    };

    const CogsToolbar = ({ productId }) => (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Add COGS">
            <span>
                <IconButton aria-label="addCogs" onClick={() => handleAddCogs(productId)}>
                    <AddIcon />
                </IconButton>
            </span>
            </Tooltip>
            <Tooltip title="Delete COGS">
            <span>
                <IconButton aria-label="deleteCogs" onClick={handleDeleteCogs} disabled={!selectedCogs}>
                    <DeleteIcon />
                </IconButton>
            </span>
            </Tooltip>
        </GridToolbarContainer>
    );

    const getDetailPanelContent = ({ row }) => {
        if (!row.cogs || row.cogs.length === 0) {
            return <Typography sx={{ p: 2 }}>No COGS data available</Typography>;
        }

        return (
            <Stack sx={{ py: 2, height: 1, boxSizing: 'border-box' }} direction="column">
                <Paper sx={{ flex: 1, mx: 'auto', width: '90%', p: 1 }}>
                    <Stack direction="column" spacing={1} sx={{ height: 1 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <DataGridPro
                                density="compact"
                                columns={cogsColumns}
                                rows={row.cogs}
                                hideFooter
                                rowSelectionModel={selectedCogs ? [selectedCogs.id] : []}
                                onRowSelectionModelChange={(selectionModel) =>
                                    handleCogsRowSelection(selectionModel, row.cogs)
                                }
                                slots={{ toolbar: CogsToolbar }}
                                slotProps={{
                                    toolbar: {
                                        productId: row.productId,
                                        selectedCogs: selectedCogs?.length > 0 ? selectedCogs : [],
                                        onAddCogs: handleAddCogs,
                                        onDeleteCogs: handleDeleteCogs,
                                    }
                                }}
                            />
                        </div>
                    </Stack>
                </Paper>
            </Stack>
        );
    };

    const getDetailPanelHeight = ({ row }) => (row.cogs.length > 0 ? row.cogs.length * 40 + 120 : 60);

    const yearOptions = useMemo(() => {
        const years = getAvailableFiscalYears(rows, startMonth, getCurrentFiscalYear(startMonth));
        if (!years.includes(fiscalYear)) {
            return [...years, fiscalYear].sort((a, b) => a - b);
        }
        return years;
    }, [rows, startMonth, fiscalYear]);

    const toolbarProps = {
        selectedProduct,
        onAdd: handleAddClick,
        onDelete: handleDeleteClick,
        onEdit: handleEditClick,
    };

    return (
        <Box sx={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, pt: 2, pb: 2, flexShrink: 0 }}>
                <Tabs
                    value={viewMode}
                    onChange={(_event, value) => setViewMode(value)}
                    aria-label="product view"
                >
                    <Tab label="List" value="list" />
                    <Tab label="Monthly" value="monthly" />
                </Tabs>
                {viewMode === 'monthly' && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="product-cogs-year-label">Year</InputLabel>
                        <Select
                            labelId="product-cogs-year-label"
                            id="product-cogs-year"
                            label="Year"
                            value={fiscalYear}
                            onChange={(event) => setFiscalYear(Number(event.target.value))}
                        >
                            {yearOptions.map((year) => (
                                <MenuItem key={year} value={year}>
                                    {formatFiscalYearLabel(startMonth, year)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>
            <Box sx={{flex: 1, minHeight: 0, width: '100%'}}>
            {viewMode === 'monthly' ? (
                <ProductCogsMonthlyView
                    rows={rows}
                    startMonth={startMonth}
                    fiscalYear={fiscalYear}
                    loading={loading}
                    selectedRowId={selectedProduct?.id ?? null}
                    onRowSelectionModelChange={handleRowSelection}
                    toolbar={EditToolbar}
                    toolbarProps={toolbarProps}
                    columnWidths={columnWidths}
                    onColumnResize={handleColumnResize}
                />
            ) : (
            <DataGridPro
                rows={rows}
                columns={productColumns}
                getRowId={(row) => row.id}
                loading={loading}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            productId: false,
                            hierarchyLevel1: false,
                            hierarchyLevel2: false,
                            hierarchyLevel3: false
                        },
                    },
                }}
                pagination
                rowSelectionModel={selectedProduct ? [selectedProduct.id] : []}
                onRowSelectionModelChange={handleRowSelection}
                onColumnResize={handleColumnResize}
                slots={{
                    toolbar: EditToolbar,
                    loadingOverlay: LinearProgress,
                }}
                slotProps={{
                    toolbar: toolbarProps
                }}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                sx={{
                    '& .non-editable': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'not-allowed',
                    },
                }}
            />
            )}
            </Box>

            {/* Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>Edit Product</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        freeSolo
                        options={availableBrands}
                        value={editData.brand}
                        onChange={(event, newValue) => {
                            setEditData({...editData, brand: newValue || ''});
                        }}
                        onInputChange={(event, newInputValue) => {
                            setEditData({...editData, brand: newInputValue});
                        }}
                        loading={loadingBrands}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Brand"
                                margin="dense"
                                fullWidth
                                slotProps={{ 
                                    htmlInput: { 
                                        ...params.inputProps,
                                        maxLength: 50 
                                    } 
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        freeSolo
                        options={availableGroups}
                        value={editData.groupName}
                        onChange={(event, newValue) => {
                            setEditData({...editData, groupName: newValue || ''});
                        }}
                        onInputChange={(event, newInputValue) => {
                            setEditData({...editData, groupName: newInputValue});
                        }}
                        loading={loadingGroups}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Group"
                                margin="dense"
                                fullWidth
                                slotProps={{ 
                                    htmlInput: { 
                                        ...params.inputProps,
                                        maxLength: 100 
                                    } 
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="Pack Size"
                        type="number"
                        value={editData.packSize}
                        onChange={(e) => setEditData({...editData, packSize: e.target.value})}
                        fullWidth
                        margin="dense"
                        error={Boolean(packSizeErrorText)}
                        helperText={packSizeErrorText}
                        slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    />
                    <TextField
                        label="Product End Date"
                        type="date"
                        value={editData.endDate}
                        onChange={(e) => setEditData({...editData, endDate: e.target.value})}
                        fullWidth
                        InputLabelProps={{shrink: true}}
                        margin="dense"
                        error={Boolean(endDateErrorText)}
                        helperText={endDateErrorText}
                    />
                    {hasIntegrationProviders && !selectedProduct?.integrationProviderName && (
                        <UnlinkedProductDropdown
                            options={externalProducts}
                            value={editData.externalProductId}
                            onChange={(externalProductId) =>
                                setEditData({ ...editData, externalProductId })
                            }
                            loading={loadingExternalProducts}
                            description="Link this product to a 3rd party integration"
                        />
                    )}
                    {hasIntegrationProviders && selectedProduct?.integrationProviderName && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Integration: {selectedProduct.integrationProviderName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                This product is linked to an external integration and cannot be modified.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary" disabled={isSubmittingEditProduct}>
                        Cancel
                    </Button>
                    <Button onClick={handleDialogSubmit} color="primary" disabled={isSubmittingEditProduct}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCreateDialogClose}>
                <DialogTitle>Create Product</DialogTitle>
                <DialogContent>
                    <TextField
                        label="SKU"
                        value={createData.skuNumber}
                        onChange={(e) => setCreateData({...createData, skuNumber: e.target.value})}
                        fullWidth
                        margin="dense"
                        slotProps={{ htmlInput: { maxLength: 50 } }}
                        error={Boolean(createErrorTexts.skuNumber)}
                        helperText={createErrorTexts.skuNumber}
                    />
                    <TextField
                        label="Description"
                        value={createData.skuDescription}
                        onChange={(e) => setCreateData({...createData, skuDescription: e.target.value})}
                        fullWidth
                        margin="dense"
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                        error={Boolean(createErrorTexts.skuDescription)}
                        helperText={createErrorTexts.skuDescription}
                    />
                    <Autocomplete
                        freeSolo
                        options={availableBrands}
                        value={createData.brand}
                        onChange={(event, newValue) => {
                            setCreateData({...createData, brand: newValue || ''});
                        }}
                        onInputChange={(event, newInputValue) => {
                            setCreateData({...createData, brand: newInputValue});
                        }}
                        loading={loadingBrands}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Brand"
                                margin="dense"
                                fullWidth
                                error={Boolean(createErrorTexts.brand)}
                                helperText={createErrorTexts.brand}
                                slotProps={{ 
                                    htmlInput: { 
                                        ...params.inputProps,
                                        maxLength: 50 
                                    } 
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        freeSolo
                        options={availableGroups}
                        value={createData.groupName}
                        onChange={(event, newValue) => {
                            setCreateData({...createData, groupName: newValue || ''});
                        }}
                        onInputChange={(event, newInputValue) => {
                            setCreateData({...createData, groupName: newInputValue});
                        }}
                        loading={loadingGroups}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Group"
                                margin="dense"
                                fullWidth
                                slotProps={{ 
                                    htmlInput: { 
                                        ...params.inputProps,
                                        maxLength: 100 
                                    } 
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="Unit COGS"
                        type="number"
                        value={createData.unitCogs}
                        onChange={(e) => setCreateData({...createData, unitCogs: e.target.value})}
                        fullWidth
                        margin="dense"
                        error={Boolean(createErrorTexts.unitCogs)}
                        helperText={createErrorTexts.unitCogs}
                        slotProps={{ htmlInput: { min: 0.001, step: 0.001 } }}
                    />
                    <TextField
                        label="Pack Size"
                        type="number"
                        value={createData.packSize}
                        onChange={(e) => setCreateData({...createData, packSize: e.target.value})}
                        fullWidth
                        margin="dense"
                        error={Boolean(createErrorTexts.packSize)}
                        helperText={createErrorTexts.packSize}
                        slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    />
                    <TextField
                        label="Start Date"
                        type="date"
                        value={createData.startDate}
                        onChange={(e) => setCreateData({...createData, startDate: e.target.value})}
                        fullWidth
                        margin="dense"
                        error={Boolean(createErrorTexts.startDate)}
                        helperText={createErrorTexts.startDate}
                        InputLabelProps={{shrink: true}}
                    />
                    {hasIntegrationProviders && (
                        <UnlinkedProductDropdown
                            options={externalProducts}
                            value={createData.externalProductId}
                            onChange={(externalProductId) =>
                                setCreateData({ ...createData, externalProductId })
                            }
                            loading={loadingExternalProducts}
                            onOpen={() => {
                                if (externalProducts.length === 0 && !loadingExternalProducts) {
                                    fetchExternalProducts();
                                }
                            }}
                            description="Link this product to an external integration"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateDialogClose} color="secondary" disabled={isSubmittingCreateProduct}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreateDialogSubmit} color="primary" disabled={isSubmittingCreateProduct}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={addCogsDialogOpen}
                onClose={handleAddCogsDialogClose}
                disableEscapeKeyDown={isSubmittingAddCogs}
            >
                <DialogTitle>Add Product COGS</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Unit COGS"
                        type="number"
                        value={newCogsData.unitCogs}
                        onChange={(e) => setNewCogsData({ ...newCogsData, unitCogs: e.target.value })}
                        fullWidth
                        margin="dense"
                        error={Boolean(cogsErrorTexts.unitCogs)}
                        helperText={cogsErrorTexts.unitCogs}
                        disabled={isSubmittingAddCogs}
                        slotProps={{ htmlInput: { min: 0.001, step: 0.001 } }}
                    />
                    <Select
                        value={newCogsData.startDate}
                        onChange={(e) => setNewCogsData({ ...newCogsData, startDate: e.target.value })}
                        fullWidth
                        margin="dense"
                        displayEmpty
                        error={Boolean(cogsErrorTexts.startDate)}
                        variant="outlined"
                        disabled={isSubmittingAddCogs}
                    >
                        <MenuItem value="" disabled>Select a Start Date</MenuItem>
                        {futureWeeksOptions.map((week) => (
                            <MenuItem key={week} value={week}>
                                {formatDate(week)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography variant="caption" color="error">
                        {cogsErrorTexts.startDate}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddCogsDialogClose} color="secondary" disabled={isSubmittingAddCogs}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddCogsSubmit} color="primary" disabled={isSubmittingAddCogs}>
                        {isSubmittingAddCogs ? "Adding..." : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    onClose={handleSnackbarClose}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleSnackbarClose}/>
                </Snackbar>
            )}
        </Box>
    );
}

export default ProductTable;
